import React, { useState, useEffect } from 'react';
import Modal from 'react-bootstrap/lib/Modal';
import Field from 'redux-form/lib/Field';
import moment from 'moment';
import { css } from '@emotion/core';
import { maxLength } from 'anchor-website-common/helpers/serverRenderingUtils';
import { useFetchPodcastMetadata } from 'client/hooks/useFetchPodcastMetadata';
import { useOptimizelyFeature } from 'hooks/useOptimizelyFeature';
import { EmailVerificationModal } from 'screens/SettingsScreen/components/EmailVerificationModal';
import { AnchorAPI } from 'modules/AnchorAPI';
import { useFeatureFlagsCtx } from 'contexts/FeatureFlags';
import { getIsEPEnabled } from 'modules/FeatureFlags';
import { useFetchDistributionData } from 'client/hooks/distribution/useFetchDistributionData';
import Box from 'shared/Box';
import { EpisodeState } from 'client/types/Episodes';
import { Spinner } from 'client/shared/Spinner';
import { Metadata } from 'client/types/Metadata';
import { Button } from 'shared/Button/NewButton';
import { FieldDatePicker } from 'components/FieldDatePicker';
import { PreDistributionModal } from 'screens/PreDistributionModalScreen/PreDistributionModal';
import Text from 'shared/Text';
import {
  FieldInputWithRedux,
  FieldRadioToggleWithRedux,
  FieldToggleWithRedux,
} from 'components/Field';
import { FieldLabel } from 'components/FieldLabel';
import { HorizontalRule } from 'components/HorizontalRule';
import { DEFAULT_IMAGE } from 'client/onboarding';
import { UnpublishEpisodeErrorModal } from 'components/UnpublishEpisodeErrorModal';
import { ResendEmailWarning } from 'components/ResendEmailWarning';
import { FieldRichTextEditorWithRedux } from './components/FieldRichTextEditor/FieldRichTextEditorWithRedux';
import { InteractiveQuestion } from './components/InteractiveQuestion';
import { InteractivePoll } from './components/InteractivePoll';
import { MusicNotAllowedErrorModal } from './components/MusicNotAllowedErrorModal';
import { DistributeToSpotifyModal } from './components/DistributeToSpotifyModal';
import events from './events/index';
import {
  PublishEpisodeHeader,
  PublishMusicHeader,
  PublishDateContainer,
  FieldToggleContainer,
  MainOptionsSection,
  MobileButtonContainer,
  SubscriptionImageLabel,
  SectionWrapper,
  DraftButton,
  SaveButton,
  FormContainer,
  ResendEmailContainer,
  CustomizeOptionsHeaderContainer,
  CustomizeOptionsHeader,
  CustomizeOptionsFormContainer,
  CustomizeOptionsFieldContainer,
  Form,
  Header,
  HeaderContainer,
  DesktopButtonContainer,
  EditImageContainer,
  EditImage,
  InlineField,
  Label,
  ErrorAlert,
  NumberInputStyles,
  ModalButton,
  InlineLabelStyles,
  SpinnerContainer,
} from './styles';
import {
  FormValues,
  SubmitButtonTitleType,
  EpisodeEditorPublishProps,
  EpisodeAudioProps,
} from './types';
import { NoTaxiDistributionErrorModal } from './components/NoTaxiDistributionErrorModal';

const maxLength200 = maxLength(200);
const maxLength4000 = maxLength(4000);
const emptyStringToNull = (value: string) => (value === '' ? null : value);
const UNVERIFIED = 'unverified'; // TODO: Extract this value from the UserVerificationState type in AnchorAPI.verifyUserEmail

export const EpisodeEditorPublishErrors = {
  MUSIC_NOT_ALLOWED: 'MUSIC_NOT_ALLOWED',
};

const getEpisodeState = (values: FormValues): EpisodeState => {
  if (!values) return null;
  const { publishOn } = values;
  const now = moment().toString();
  if (!publishOn) return 'isDraft';
  if (moment(now).isAfter(publishOn)) return 'isPublished';
  if (moment(publishOn).isAfter(now)) return 'isScheduled';
  return null;
};

const getFormatedTime = (dateTime: string) =>
  moment.utc(dateTime).local().format('M/D/YY [at] h:mm A');

const validateRequiredField = (value: any) =>
  value ? undefined : 'Required field';
const validateDescriptionField = (value: string) =>
  value && value !== '<p><br></p>' && value !== '<p></p>'
    ? undefined
    : 'Required field';

const EpisodeEditorPublishWithHooks = (props: any) => {
  const {
    actions: {
      receiveLocalUploadedImage,
      setPublishOnValue,
      fetchMyPodcastMetadata,
      fetchUserVerificationState,
      dismissAudioError,
    },
    match: { path, params },
    error,
    clearSubmitErrors,

    episodeEditorScreen: {
      podcastEpisodeId,
      addAudioError,
      isFetchingEpisode,
      metadata: { episodeImage },
      episodeAudios,
    },
    formValues,
    handleSubmit,
    onSubmit,
    podcastEditor: {
      podcastImage,
      podcastName,
      podcastDescription,
      language,
      itunesCategory,
    },
    initialValues,
    userId,
    containsMusicSegments,
    stationId,
    isPaywallsEnabled,
    isSpotifyExclusive,
    isPollsEnabled,
    isListenerQuestionEnabled,
    userVerificationState,
    isOptedIntoSpotifyDistribution,
    isManualDistributionEnabled,
    onEpisodeEditorPopupView,
    submitting,
    isMusicAndTalkFastTrackEnabled,
    spotifyDistributionStatus,
    isRssFeedEnabled,
    hasOnlyPublishedMTEpisodes,
    canEnableRss,
    distributionEligibilityForPaywalls,
    podcastStatus,
    isExemptFromDistributionPrompt,
  } = props;

  const initialEpisodeState = getEpisodeState(initialValues);

  const [
    submitButtonTitle,
    setSubmitButtonTitle,
  ] = useState<SubmitButtonTitleType>(null);
  const [doPersistError, setDoPersistError] = useState(false);
  const [
    isEmailVerificationModalOpen,
    setIsEmailVerificationModalOpen,
  ] = useState(false);
  const [
    emailVerificationRequestSent,
    setEmailVerificationRequestSent,
  ] = useState(false);
  const [canUnPublish, setCanUnPublish] = useState(true);
  const [
    showUnpublishEpisodeErrorModal,
    setShowUnpublishEpisodeErrorModal,
  ] = useState(false);
  const [
    isSpotifyDistributionModalOpen,
    setIsSpotifyDistributionModalOpen,
  ] = useState(false);
  const [isPreDistributionModalOpen, setIsPreDistributionModalOpen] = useState(
    false
  );
  const [
    isPublishingForTheFirstTime,
    setIsPublishingForTheFirstTime,
  ] = useState(false);
  const [
    isNoTaxiDistributionErrorModalOpen,
    setIsNoTaxiDistributionErrorModalOpen,
  ] = useState(false);

  useEffect(() => {
    AnchorAPI.fetchMyPodcastMetadata().then(({ episodes = [] }: Metadata) => {
      const now = new Date();

      const publishedEpisodes = episodes.filter(episode => {
        // Unpublishing the only RSS episode breaks the user's feed on external platforms
        // Users can unpublish TAXI episodes without breaking their RSS feed
        const {
          publishOn,
          containsMusicSegments: episodeContainsMusicSegments,
        } = episode;
        return (
          publishOn &&
          new Date(publishOn) < now &&
          !episodeContainsMusicSegments
        );
      });

      if (publishedEpisodes.length === 1) {
        if (params.podcastEpisodeId === publishedEpisodes[0].episodeId) {
          setCanUnPublish(false);
        }
      }
    });
    receiveLocalUploadedImage(null);
    fetchUserVerificationState();
    const requestedPodcastEpisodeId =
      (params && params.podcastEpisodeId) || null;
    if (!podcastImage) {
      fetchMyPodcastMetadata();
    }

    setIsPublishingForTheFirstTime(path === '/dashboard/episode/new/publish');

    if (requestedPodcastEpisodeId) {
      events.trackMetadataEditPageView();
    } else {
      events.publishEpisodePageViewed();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getDraftButtonText = (episodeState: EpisodeState) => {
    switch (episodeState) {
      case 'isDraft':
        return isPublishingForTheFirstTime
          ? 'Save as a draft'
          : 'Update saved draft';
      case 'isScheduled':
        return 'Revert to draft';
      case 'isPublished':
        return 'Revert to draft';
      default:
        return null;
    }
  };

  const getPublishButtonText = (episodeState: EpisodeState) => {
    const { publishOn: initialPublishOn } = initialValues;
    if (!formValues) return ['', ''];
    const { publishOn } = formValues;
    const isPublishOnValueUpdated = !moment(initialPublishOn).isSame(publishOn);
    const isPublishOnValueNeverSet = !publishOn && !initialPublishOn;
    const publishText = [
      !isOptedIntoSpotifyDistribution && isManualDistributionEnabled
        ? 'Next'
        : 'Publish now',
      'Publishing...',
    ];
    const updateText = ['Update episode', 'Updating...'];
    const scheduleText = ['Schedule episode', 'Scheduling...'];
    switch (episodeState) {
      case 'isDraft':
        if (isPublishOnValueNeverSet) return publishText;
        if (isPublishingForTheFirstTime) {
          return publishOn ? scheduleText : publishText;
        }
        return isPublishOnValueUpdated ? scheduleText : publishText;
      case 'isScheduled':
        return publishOn ? updateText : publishText;
      case 'isPublished':
        return publishOn && isPublishOnValueUpdated ? scheduleText : updateText;
      default:
        return updateText;
    }
  };

  const getDefaultSubmitType = () => {
    if (!formValues) return 'draft';
    return !formValues.publishOn ? 'draft' : 'publish';
  };

  const handleClickDismissAudioErrorModal = () => {
    if (dismissAudioError) dismissAudioError();
    setDoPersistError(true);
  };

  const handleShowAudioErrorModal = () => {
    onEpisodeEditorPopupView('add_audio_error');
  };

  const handleShowCropperModal = () => {
    onEpisodeEditorPopupView('image_editor');
  };

  const renderSubHeaderContent = (episodeState: EpisodeState) => {
    const { created, publishOn } = initialValues;
    switch (episodeState) {
      case 'isDraft':
        return isPublishingForTheFirstTime ? null : (
          <Text color="#5f6369" size="lg">
            Created on: {getFormatedTime(created)}
          </Text>
        );
      case 'isScheduled':
        return (
          <Text color="#5f6369" size="lg">
            Scheduled for: {getFormatedTime(publishOn)}
          </Text>
        );
      case 'isPublished':
        return (
          <Text color="#5f6369" size="lg">
            Published on: {getFormatedTime(publishOn)}
          </Text>
        );
      default:
        return null;
    }
  };

  const isUnverified = () => {
    return userVerificationState === UNVERIFIED;
  };

  const handleSaveDraft = () => {
    return handleSubmit((values: FormValues) => {
      if (!canUnPublish) {
        setShowUnpublishEpisodeErrorModal(true);
        return null;
      }
      setSubmitButtonTitle('draft');
      if (isPublishingForTheFirstTime) {
        // if in new publish view we want to fire off the event that has the location as 'episode_edit'
        // otherwise fire off event that has location as 'episode_metadata_edit'
        events.trackSaveAsDraftButtonClicked();
      } else {
        events.trackMetadataEditSaveAsDraftButtonClicked();
      }

      return onSubmit(
        {
          ...values,
          isDraft: true,
          publishOn: null,
        },
        initialValues
      );
    });
  };

  const renderDraftButton = (episodeState: EpisodeState | null) => {
    return (
      <DraftButton
        kind="button"
        type="submit"
        onClick={handleSaveDraft()}
        isDisabled={submitting || doPersistError}
      >
        {submitButtonTitle === 'draft' && submitting
          ? 'Saving...'
          : getDraftButtonText(episodeState)}
      </DraftButton>
    );
  };

  const isPodcastSetup = () => {
    return (
      podcastName &&
      podcastDescription &&
      language &&
      itunesCategory &&
      podcastImage
    );
  };

  const renderSaveButtonSpotifyExclusive = () => {
    // Only music episodes need review before publishing
    // Partnered M+T shows do not (isMusicAndTalkFastTrackEnabled)
    const needsReview =
      containsMusicSegments && !isMusicAndTalkFastTrackEnabled;
    const idleButtonCopy =
      !isOptedIntoSpotifyDistribution && isManualDistributionEnabled
        ? 'Next'
        : needsReview
        ? 'Submit episode for review'
        : 'Publish';
    return (
      <Button
        kind="button"
        color="purple"
        onClick={handleSubmit(async (values: FormValues) => {
          setSubmitButtonTitle('publish');

          events.publishMenuButtonClicked({ location: 'episode_edit' });

          // new distribution logic
          if (isManualDistributionEnabled) {
            if (!isPodcastSetup() || !isOptedIntoSpotifyDistribution) {
              return setIsPreDistributionModalOpen(true);
            }
          } else {
            // old logic
            try {
              const {
                externalUrls: { spotify },
                podcastDistributionStatus,
              } = await AnchorAPI.fetchDistributionData({
                currentUserId: userId,
              });

              if (
                podcastDistributionStatus === 'noActionTaken' ||
                !isPodcastSetup()
              ) {
                return setIsPreDistributionModalOpen(true);
              }
              if (
                !spotify ||
                (spotifyDistributionStatus === 'off' &&
                  podcastDistributionStatus === 'optedout')
              ) {
                return setIsSpotifyDistributionModalOpen(true);
              }
            } catch (err) {
              // https://github.com/Microsoft/TypeScript/issues/20024
              throw new Error((err as Error).message);
            }
          }

          return onSubmit(
            {
              ...values,
              isDraft: false,
            },
            initialValues
          );
        })}
        css={css`
          width: 270px;
          flex: 1;
        `}
      >
        {submitButtonTitle === 'publish' && submitting
          ? 'Submitting...'
          : idleButtonCopy}
      </Button>
    );
  };

  const renderSaveButton = (episodeState: EpisodeState) => {
    const { publishOn } = formValues;
    const [buttonText, processingButtonText] = getPublishButtonText(
      episodeState
    );

    return (
      <SaveButton
        kind="button"
        color="purple"
        css={css`
          width: 270px;
        `}
        onClick={handleSubmit((values: FormValues) => {
          const now = new Date();
          const isScheduled = moment(publishOn).toDate() > now;
          if (!canUnPublish && isScheduled) {
            setShowUnpublishEpisodeErrorModal(true);
            return null;
          }
          if (!episodeAudios.length) return null;
          const unverified = isUnverified();
          setSubmitButtonTitle('publish');
          setIsEmailVerificationModalOpen(unverified);
          setEmailVerificationRequestSent(unverified);
          setIsPublishingForTheFirstTime(!unverified);

          const isBackgroundAudioPresent = episodeAudios.some(
            (audio: EpisodeAudioProps) => {
              const {
                audioTransformationName,
                url,
                derivedFromAudioId,
              } = audio;
              return (
                url &&
                audioTransformationName === 'backgroundTrack2' &&
                Boolean(derivedFromAudioId)
              );
            }
          );
          // only fire publish events if an episode is going from non-published to published
          // aka isDraft === true or it doesn't have a publishOn date already
          if ((!isUnverified() && values.isDraft) || !values.publishOn) {
            events.episodePublished(userId);
            if (isBackgroundAudioPresent) {
              events.episodePublishedWithBackgroundTracks({
                podcastEpisodeId,
              });
            }
          }

          if (isUnverified()) return null;

          if (isManualDistributionEnabled) {
            if (
              !isPodcastSetup() ||
              (!isOptedIntoSpotifyDistribution &&
                !isExemptFromDistributionPrompt) ||
              (!isRssFeedEnabled && hasOnlyPublishedMTEpisodes && canEnableRss)
            ) {
              setIsPreDistributionModalOpen(true);
              return null;
            }
          }

          return onSubmit(
            {
              ...values,
              isDraft: false,
            },
            initialValues
          );
        })}
        isDisabled={
          submitting ||
          doPersistError ||
          !episodeAudios.length ||
          emailVerificationRequestSent
        }
      >
        {submitButtonTitle === 'publish' && submitting
          ? processingButtonText
          : buttonText}
      </SaveButton>
    );
  };

  const handlePublish = () => {
    return handleSubmit((values: FormValues) => {
      setSubmitButtonTitle('publish');
      events.publishMenuButtonClicked({ location: 'episode_edit' });

      return onSubmit(
        {
          ...values,
          isDraft: false,
        },
        initialValues
      );
    });
  };

  const defaultSubmitType = getDefaultSubmitType();

  return (
    <FormContainer>
      {isUnverified() && (
        <ResendEmailContainer>
          <ResendEmailWarning userId={userId} />
        </ResendEmailContainer>
      )}
      <Form
        method="POST"
        onSubmit={handleSubmit((values: FormValues) => {
          setSubmitButtonTitle(defaultSubmitType);
          events.publishMenuButtonClicked({ location: 'episode_edit' });

          return onSubmit(
            {
              ...values,
              isDraft: defaultSubmitType === 'draft',
            },
            initialValues
          );
        })}
      >
        {isFetchingEpisode || !formValues ? (
          <SpinnerContainer>
            <Spinner color="gray" />
          </SpinnerContainer>
        ) : (
          <React.Fragment>
            <MainOptionsSection>
              {isSpotifyExclusive ? (
                <div>
                  <PublishMusicHeader>
                    <HeaderContainer>
                      <Header>Submit to Spotify</Header>
                    </HeaderContainer>
                    <DesktopButtonContainer>
                      {renderDraftButton(initialEpisodeState)}
                      {renderSaveButtonSpotifyExclusive()}
                    </DesktopButtonContainer>
                  </PublishMusicHeader>
                  {containsMusicSegments && (
                    <p
                      css={css`
                        margin-bottom: 24px;
                        font-size: 1.6rem;
                      `}
                    >
                      Since this episode contains songs, it will only be
                      available on Spotify, and needs to be reviewed before it
                      goes live. We’ll let you know as soon as it’s approved. If
                      you schedule this episode for a future date, we’ll review
                      your episode as quickly as possible, but can’t guarantee
                      it will be done before your scheduled publishing
                      date/time.
                    </p>
                  )}
                </div>
              ) : (
                <PublishEpisodeHeader>
                  <HeaderContainer>
                    <Header>Episode options</Header>
                    <Box marginTop={8}>
                      {renderSubHeaderContent(initialEpisodeState)}
                    </Box>
                  </HeaderContainer>
                  <DesktopButtonContainer>
                    {renderDraftButton(initialEpisodeState)}
                    {renderSaveButton(initialEpisodeState)}
                  </DesktopButtonContainer>
                </PublishEpisodeHeader>
              )}
              {error && error !== EpisodeEditorPublishErrors.MUSIC_NOT_ALLOWED && (
                <ErrorAlert>
                  <span>
                    We&apost;re unable to publish your episode. Revert to draft
                    to make any edits and submit again.
                  </span>
                </ErrorAlert>
              )}
              {error === EpisodeEditorPublishErrors.MUSIC_NOT_ALLOWED && (
                <MusicNotAllowedErrorModal onDismiss={clearSubmitErrors} />
              )}
              <Box display="flex" direction="column">
                <Box marginBottom={18}>
                  <FieldLabel htmlFor="title">
                    <Label>Episode title*</Label>
                  </FieldLabel>
                  <Field
                    name="title"
                    type="text"
                    component={FieldInputWithRedux}
                    maxCharacterLength={200}
                    normalize={maxLength200}
                    placeholder="What do you want to call this episode?"
                    validate={validateRequiredField}
                  />
                </Box>
                <Box marginBottom={18}>
                  <Field
                    label="Episode description*"
                    name="description"
                    type="textarea"
                    component={FieldRichTextEditorWithRedux}
                    maxLength={4000}
                    normalize={maxLength4000}
                    placeholder="What else do you want your listeners to know?"
                    validate={validateDescriptionField}
                  />
                </Box>
                <PublishDateContainer>
                  <FieldDatePicker
                    label="Publish date:"
                    value={formValues.publishOn}
                    setValue={setPublishOnValue}
                  />
                  {isPaywallsEnabled && !containsMusicSegments && (
                    <FieldToggleContainer>
                      <Field
                        name="isPW"
                        label="Subscription only"
                        component={FieldToggleWithRedux}
                        onChange={() => {
                          events.episodeSubscriptionToggled({
                            podcastEpisodeId,
                            toggledOn: !formValues.isPW,
                          });
                        }}
                      />
                      <FieldLabel
                        htmlFor="isPW"
                        css={css`
                          color: #292f36;
                          font-size: 16px;
                          font-weight: 500;
                        `}
                      >
                        Subscription only
                      </FieldLabel>
                    </FieldToggleContainer>
                  )}
                </PublishDateContainer>
                <MobileButtonContainer>
                  {renderDraftButton(initialEpisodeState)}
                  {renderSaveButton(initialEpisodeState)}
                </MobileButtonContainer>
              </Box>
            </MainOptionsSection>
            <SectionWrapper>
              <HorizontalRule marginTop={46} marginBottom={46} />
            </SectionWrapper>
            {podcastEpisodeId && isPollsEnabled && (
              <InteractivePoll episodeId={podcastEpisodeId} userId={userId} />
            )}
            {podcastEpisodeId && isListenerQuestionEnabled && (
              <InteractiveQuestion episodeId={podcastEpisodeId} />
            )}
            <SectionWrapper>
              <CustomizeOptionsHeaderContainer>
                <Box marginRight={6}>
                  <CustomizeOptionsHeader>
                    Customize this episode
                  </CustomizeOptionsHeader>
                </Box>
                <Text color="#7f8287" size="md">
                  (Optional)
                </Text>
              </CustomizeOptionsHeaderContainer>
              <CustomizeOptionsFormContainer>
                <Box
                  display="flex"
                  justifyContent="center"
                  dangerouslySetInlineStyle={{ flex: 1 }}
                >
                  <EditImageContainer>
                    <EditImage
                      onImageLocallyUploaded={receiveLocalUploadedImage}
                      originalImage={
                        episodeImage || podcastImage || DEFAULT_IMAGE
                      }
                      uploadLinkLabel="Upload new episode art"
                      onShowCropper={handleShowCropperModal}
                      screen="episode_edit"
                      clickEventName="change_image_clicked"
                      altText={`"${formValues.title}" cover`}
                    />
                    {isPaywallsEnabled &&
                      !containsMusicSegments &&
                      formValues.isPW && (
                        <SubscriptionImageLabel>
                          Subscription
                        </SubscriptionImageLabel>
                      )}
                  </EditImageContainer>
                </Box>
                <CustomizeOptionsFieldContainer>
                  <InlineField>
                    <FieldLabel
                      htmlFor="podcastSeasonNumber"
                      cssProp={InlineLabelStyles}
                    >
                      <Label>Season number</Label>
                    </FieldLabel>
                    <Field
                      name="podcastSeasonNumber"
                      type="number"
                      css={NumberInputStyles}
                      component={FieldInputWithRedux}
                      min={0}
                      normalize={emptyStringToNull}
                      labelWidth={160}
                      isInline
                    />
                  </InlineField>
                  <InlineField>
                    <FieldLabel
                      htmlFor="podcastEpisodeNumber"
                      cssProp={InlineLabelStyles}
                    >
                      <Label>Episode number</Label>
                    </FieldLabel>
                    <Field
                      label="Episode number"
                      name="podcastEpisodeNumber"
                      type="number"
                      css={NumberInputStyles}
                      component={FieldInputWithRedux}
                      min={0}
                      normalize={emptyStringToNull}
                      labelWidth={160}
                      isInline
                    />
                  </InlineField>
                  <Box marginBottom={30} maxWidth={369}>
                    <Field
                      label={<Label>Episode type</Label>}
                      name="podcastEpisodeType"
                      component={FieldRadioToggleWithRedux}
                      options={[
                        { label: 'Full', value: 'full' },
                        { label: 'Trailer', value: 'trailer' },
                        { label: 'Bonus', value: 'bonus' },
                      ]}
                    />
                  </Box>
                  <Box maxWidth={246}>
                    <Field
                      label={<Label>Content</Label>}
                      name="podcastEpisodeIsExplicit"
                      component={FieldRadioToggleWithRedux}
                      options={[
                        { label: 'Clean', value: false },
                        { label: 'Explicit', value: true },
                      ]}
                    />
                  </Box>
                </CustomizeOptionsFieldContainer>
              </CustomizeOptionsFormContainer>
            </SectionWrapper>
          </React.Fragment>
        )}
      </Form>
      <Modal
        show={addAudioError && addAudioError.isModal}
        onHide={handleClickDismissAudioErrorModal}
        onEntered={handleShowAudioErrorModal}
      >
        {addAudioError && (
          <Modal.Body key="errorPrompt" className="text-center">
            <h2>{addAudioError.title}</h2>
            <p>{addAudioError.subTitle}</p>
          </Modal.Body>
        )}
        <Modal.Footer key="errorFooter">
          <ModalButton
            color="purple"
            onClick={handleClickDismissAudioErrorModal}
            kind="link"
            href="/dashboard/episode/new/upload"
          >
            Try uploading again
          </ModalButton>
          <ModalButton onClick={handleClickDismissAudioErrorModal}>
            Cancel
          </ModalButton>
        </Modal.Footer>
      </Modal>
      {isEmailVerificationModalOpen && (
        <EmailVerificationModal
          onClickClose={handleSaveDraft()}
          type="publish"
        />
      )}
      {showUnpublishEpisodeErrorModal && (
        <UnpublishEpisodeErrorModal
          action="unpublish"
          onClose={() => {
            setShowUnpublishEpisodeErrorModal(false);
          }}
        />
      )}
      {isSpotifyDistributionModalOpen && (
        <DistributeToSpotifyModal
          onClickClose={(e: React.MouseEvent<HTMLButtonElement>) => {
            setIsSpotifyDistributionModalOpen(false);
            handleSaveDraft()(e);
          }}
          onSubmit={async e => {
            try {
              await AnchorAPI.requestSpotifyOnlyDistribution({
                stationId,
              });
              // after distributing to Spotify we can submit the episode
              // call the form's submit function from the modal
              handlePublish()(e);
            } catch (err) {
              throw new Error((err as Error).message);
            }
          }}
          podcastImage={podcastImage}
        />
      )}
      {isNoTaxiDistributionErrorModalOpen && (
        <NoTaxiDistributionErrorModal
          onClickClose={handleSubmit((values: FormValues) => {
            setIsNoTaxiDistributionErrorModalOpen(false);
            return onSubmit(
              {
                ...values,
                isDraft: false,
              },
              initialValues
            );
          })}
        />
      )}
      {isPreDistributionModalOpen && (
        <PreDistributionModal
          containsMusicSegments={containsMusicSegments}
          onFinishAcceptDistribution={handlePublish()} // legacy
          onHideModal={() => setIsPreDistributionModalOpen(false)}
          onMTAcceptSuccess={handleSubmit((values: FormValues) => {
            setIsPreDistributionModalOpen(false);
            // in the case where a legacy account has prev opted out of the old distribution flow
            // we need to check if they have a taxi feed, if not prompt them to contact
            // support so that we can resubmit their m+t episode
            if (
              podcastStatus === 'optedout' &&
              distributionEligibilityForPaywalls === 'no_taxi_distribution'
            ) {
              return setIsNoTaxiDistributionErrorModalOpen(true);
            }
            return onSubmit(
              {
                ...values,
                isDraft: false,
              },
              initialValues
            );
          })}
          onSpotifyDistributionAcceptSuccess={handleSubmit(
            (values: FormValues) => {
              return onSubmit(
                {
                  ...values,
                  isDraft: false,
                },
                initialValues,
                { optedIntoDistribution: true, isOpenToolTip: true }
              );
            }
          )}
          onRSSAcceptClick={() => {
            setIsPreDistributionModalOpen(false);
            handleSubmit((values: FormValues) => {
              return onSubmit(
                {
                  ...values,
                  isDraft: false,
                },
                initialValues,
                { optedIntoDistribution: true }
              );
            });
          }}
        />
      )}
    </FormContainer>
  );
};

export const EpisodeEditorPublish = (props: EpisodeEditorPublishProps) => {
  const { containsMusicSegments, podcastStatus, podcast } = props;
  const {
    state: { featureFlags },
  } = useFeatureFlagsCtx();
  const [isManualDistributionEnabled] = useOptimizelyFeature(
    'manual_distribution'
  );
  const [isListenerQuestionEnabled] = useOptimizelyFeature('internal_q_a');
  const [isPollsEnabled] = useOptimizelyFeature('episode_polls');

  const { data: podcastMetadata } = useFetchPodcastMetadata();
  const { distributionData } = useFetchDistributionData();

  const isRssFeedEnabled =
    distributionData && distributionData.isRssFeedEnabled;
  const isPaywallsEnabled =
    podcastMetadata && podcastMetadata.isPWEnabled && podcastMetadata.isPWSetup;
  const isEPEnabled = getIsEPEnabled(featureFlags);
  const isSpotifyExclusive = isEPEnabled || containsMusicSegments;
  const canEnableRss = distributionData && distributionData.canEnableRss;
  const status = podcastStatus || podcast.podcastStatus;
  const spotifyDistributionStatus =
    distributionData && distributionData.spotifyDistributionStatus;
  const distributionEligibilityForPaywalls =
    podcastMetadata && podcastMetadata.distributionEligibilityForPaywalls;
  const isOptedIntoSpotifyDistribution =
    spotifyDistributionStatus === 'on' || status === 'optedinspotify';
  const isMusicAndTalkFastTrackEnabled =
    podcastMetadata && podcastMetadata.isMusicAndTalkFastTrackEnabled;
  const isImportedPodcast =
    distributionData &&
    (!!distributionData.podcastExternalSource ||
      !!distributionData.podcastExternalSourceRedirected);
  const isLegacyOptedOut =
    status === 'optedout' &&
    distributionData &&
    distributionData.spotifyDistributionStatus === 'pending';
  const isExemptFromDistributionPrompt = isLegacyOptedOut || isImportedPodcast;

  if (!featureFlags) return null;

  return (
    <EpisodeEditorPublishWithHooks
      {...props}
      isPaywallsEnabled={isPaywallsEnabled}
      isMusicAndTalkFastTrackEnabled={isMusicAndTalkFastTrackEnabled}
      spotifyDistributionStatus={spotifyDistributionStatus}
      isExemptFromDistributionPrompt={isExemptFromDistributionPrompt}
      podcastStatus={status}
      isOptedIntoSpotifyDistribution={isOptedIntoSpotifyDistribution}
      distributionEligibilityForPaywalls={distributionEligibilityForPaywalls}
      isSpotifyExclusive={isSpotifyExclusive}
      isPollsEnabled={isPollsEnabled}
      isRssFeedEnabled={isRssFeedEnabled}
      isManualDistributionEnabled={isManualDistributionEnabled}
      isListenerQuestionEnabled={isListenerQuestionEnabled}
      canEnableRss={canEnableRss}
    />
  );
};
